import type { PermissionBundle } from '@/types/role.types';
import type { ExitType } from '@/lib/exitTypes';

/** User status as returned by the API. */
export type UserStatus = 'active' | 'inactive' | 'suspended' | 'exited';
export type UserListStatusFilter = UserStatus | 'deleted';
export type EmploymentLifecycleStatus = 'probation' | 'permanent' | 'terminated';

/**
 * Which protected fields the API included for this caller. Hidden fields are
 * omitted from the payload, so `false` means "Hidden", not "Not set".
 * Absent on responses from older APIs: treat as visible.
 */
export interface UserFieldAccess {
  /** Bank details, tax info, national ID / passport, date of birth. */
  sensitive: boolean;
  /** Base salary, hourly rate, allowances and deductions. */
  payRates: boolean;
}

/** Full user entity (no password). Matches backend User schema. */
export interface User {
  _id: string;
  organizationId: string;
  email: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  avatar?: string | null;
  personalInfo?: Record<string, unknown>;
  contactInfo?: Record<string, unknown>;
  employment?: Record<string, unknown>;
  compensation?: Record<string, unknown>;
  roleId: string;
  /** Set when API returns populated roleId (e.g. GET user by id). */
  roleName?: string;
  status: UserStatus;
  isDeleted?: boolean;
  leaveBalances?: Array<Record<string, unknown>>;
  additionalPermissions?: PermissionBundle[];
  restrictedPermissions?: PermissionBundle[];
  lastLoginAt?: string;
  createdAt?: string;
  updatedAt?: string;
  fieldAccess?: UserFieldAccess;
  __v?: number;
}

/** List item for GET /users. Includes id and name for AuditLogFilters compatibility. */
export interface UserListItem {
  _id: string;
  id: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  employeeId: string;
  roleId: string;
  /** Set when API returns populated roleId (object). */
  roleName?: string;
  status: UserListStatusFilter;
  isDeleted?: boolean;
  employment?: Record<string, unknown>;
  company?: UserCompanySummary | null;
  companies?: UserCompanySummary[];
}

export interface UserCompanySummary {
  id: string;
  name: string;
}

/** Nested types for CreateUserDto / UpdateUserDto. */

export const UserGender = {
  MALE: 'MALE',
  FEMALE: 'FEMALE',
  OTHER: 'OTHER',
} as const;
export type UserGender = (typeof UserGender)[keyof typeof UserGender];

/** Stored on user profile; align with API expectations. */
export const MaritalStatus = {
  SINGLE: 'SINGLE',
  MARRIED: 'MARRIED',
  DIVORCED: 'DIVORCED',
  WIDOWED: 'WIDOWED',
  SEPARATED: 'SEPARATED',
  DOMESTIC_PARTNERSHIP: 'DOMESTIC_PARTNERSHIP',
  OTHER: 'OTHER',
} as const;
export type MaritalStatus = (typeof MaritalStatus)[keyof typeof MaritalStatus];

export type EmploymentType = 'full-time' | 'part-time' | 'contract' | 'intern';
export type WorkLocation = 'remote' | 'onsite' | 'hybrid';

export interface CreateUserEmergencyContact {
  name?: string;
  relationship?: string;
  phone?: string;
  email?: string;
}

export interface UserAddress {
  street?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
}

export interface CreateUserPersonalInfo {
  dateOfBirth?: string;
  gender?: UserGender;
  /** ISO 3166-1 alpha-2 country code. */
  nationality?: string;
  cnicNumber?: string;
  passportNumber?: string;
  maritalStatus?: MaritalStatus;
  emergencyContact?: CreateUserEmergencyContact;
}

export interface CreateUserAddress {
  street?: string;
  city?: string;
  state?: string;
  country?: string;
  postalCode?: string;
}

export interface CreateUserContactInfo {
  personalEmail?: string;
  phone?: string;
  alternatePhone?: string;
  address?: CreateUserAddress;
}

export interface UpdateMePersonalInfo {
  dateOfBirth?: string;
  gender?: string;
  nationality?: string;
  cnicNumber?: string;
  passportNumber?: string;
  maritalStatus?: string;
  emergencyContact?: CreateUserEmergencyContact;
}

export interface UpdateMeContactInfo {
  personalEmail?: string;
  phone?: string;
  alternatePhone?: string;
  address?: UserAddress;
}

export interface UpdateMePayload {
  firstName?: string;
  lastName?: string;
  personalInfo?: UpdateMePersonalInfo;
  contactInfo?: UpdateMeContactInfo;
}

export interface CreateUserEmployment {
  department?: string;
  designation?: string;
  employmentType?: EmploymentType;
  joiningDate?: string;
  workLocation?: WorkLocation;
}

/** Body for PUT /v1/users/:id/employment. Partial update. */
export interface UpdateEmploymentPayload {
  department?: string;
  designation?: string;
  employmentType?: EmploymentType;
  joiningDate?: string;
  reportingTo?: string;
  workLocation?: WorkLocation;
  employmentStatus?: EmploymentLifecycleStatus;
}

export interface CreateUserBankDetails {
  accountNumber?: string;
  bankName?: string;
  accountTitle?: string;
  branch?: string;
  branchCode?: string;
}

export interface CreateUserTaxInfo {
  /** Aligns with User.compensation.taxInfo.taxNumber in the API. */
  taxNumber?: string;
  taxBracket?: string;
  taxExempt?: boolean;
}

export type CreateUserCompensationType = 'salaried' | 'hourly';

export interface CreateUserCompensation {
  /** 'salaried' uses baseSalary; 'hourly' uses hourlyRate. Send exactly one rate field. */
  compensationType?: CreateUserCompensationType;
  baseSalary?: number;
  hourlyRate?: number;
  currency?: string;
  payFrequency?: string;
  bankDetails?: CreateUserBankDetails;
  taxInfo?: CreateUserTaxInfo;
}

/** Body for PUT /v1/users/:id/compensation (UpdateCompensationDto). Partial — send only changed fields. */
export interface UpdateCompensationPayload {
  /** 'salaried' uses baseSalary; 'hourly' uses hourlyRate. */
  compensationType?: CreateUserCompensationType;
  baseSalary?: number;
  hourlyRate?: number;
  currency?: string;
  payFrequency?: string;
  bankDetails?: CreateUserBankDetails;
  taxInfo?: CreateUserTaxInfo;
}

/** Body for POST /v1/users (CreateUserDto). roleId must be a valid MongoDB ObjectId. */
export interface CreateUserPayload {
  email: string;
  password?: string;
  firstName: string;
  lastName: string;
  roleId: string;
  personalInfo?: CreateUserPersonalInfo;
  contactInfo?: CreateUserContactInfo;
  employment?: CreateUserEmployment;
  compensation?: CreateUserCompensation;
}

/** Body for PUT /v1/users/:id (UpdateUserDto). Partial update — send only changed fields.
 *  Extra fields (employment, compensation, roleId, password) are stripped by the backend. */
export interface UpdateUserPayload {
  email?: string;
  firstName?: string;
  lastName?: string;
  avatar?: string | null;
  personalInfo?: CreateUserPersonalInfo;
  contactInfo?: CreateUserContactInfo;
}

/** Query params for GET /v1/users. */
export interface UserListQueryParams {
  roleId?: string;
  status?: UserListStatusFilter;
  department?: string;
  companyId?: string;
  companyIds?: string[];
  /** Case-insensitive partial match across firstName, lastName, email, employeeId. */
  search?: string;
  page?: number;
  limit?: number;
}

/** Body for PUT /v1/users/:id/status. */
export interface ChangeStatusPayload {
  status: Exclude<UserStatus, 'exited'>;
}

export interface ConfirmPermanentPayload {
  confirmationDate?: string;
}

export interface TerminateUserPayload {
  exitDate: string;
  exitReason?: string;
  /** Why the employment ended; the API records `terminated` when omitted. */
  exitType?: ExitType;
}

/** Body for assign-role endpoint if backend exposes it. */
export interface AssignRolePayload {
  roleId: string;
}
